import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "~/api";
import { Button } from "~/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/Dialog";
import { SpinnerIcon } from "~/components/icon";
import { SessionExpired } from "~/components/SessionExpired";
import { useAuth } from "~/contexts/auth";
import type { OrgPurgeRegion, OrgToPurge } from "~/types";
import { formatRelativeUtc } from "~/utils/time";

interface OrgsToPurgeCustomerGroup {
  customerId: string | null;
  customerName: string | null;
  customerDeactivatedAt: string | null;
  orgs: OrgToPurge[];
}

// for orgs that are not attached to a parent customer
const NO_CUSTOMER_GROUP = "__no_customer__";

function buildCustomerGroups(orgs: OrgToPurge[]): OrgsToPurgeCustomerGroup[] {
  const groups = new Map<string, OrgsToPurgeCustomerGroup>();

  for (const org of orgs) {
    const key = org.customer_id ?? NO_CUSTOMER_GROUP;
    let group = groups.get(key);
    if (!group) {
      group = {
        customerId: org.customer_id,
        customerName: org.customer_name,
        customerDeactivatedAt: org.customer_deactivated_at,
        orgs: [],
      };
      groups.set(key, group);
    }
    group.orgs.push(org);
  }

  return [...groups.values()].sort(
    (a, b) => Date.parse(a.orgs[0].purge_at) - Date.parse(b.orgs[0].purge_at),
  );
}

function pluralize(count: number, singular: string, plural = `${singular}s`) {
  return `${count.toLocaleString()} ${count === 1 ? singular : plural}`;
}

function formatUtcTimestamp(timestamp: string): string {
  return new Date(timestamp).toISOString();
}

function isPurgeRegionActionable(region: OrgPurgeRegion): boolean {
  return !region.operation_id || region.operation_status === "FAILURE";
}

function getPurgeRegionStatus(region: OrgPurgeRegion): {
  label: string;
  className: string;
} {
  if (!region.operation_id) {
    return {
      label: "No purge job",
      className: "bg-orange-100 text-orange-700",
    };
  }

  if (region.operation_status === "SUCCESS") {
    return {
      label: "Succeeded, waiting for namespace refresh",
      className: "bg-green-100 text-green-700",
    };
  }

  if (region.operation_status === "FAILURE") {
    return {
      label: "Failed",
      className: "bg-red-100 text-red-700",
    };
  }

  const status = region.operation_status
    ? region.operation_status.toLowerCase().replaceAll("_", " ")
    : "created";

  return {
    label: `Purge job ${status}`,
    className: "bg-blue-100 text-blue-700",
  };
}

function OrgToPurgeCard({
  org,
  onPurge,
}: {
  org: OrgToPurge;
  onPurge: (org: OrgToPurge) => void;
}) {
  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/orgs/${org.id}`}
              className="text-sm font-semibold text-blue-700 hover:underline"
            >
              {org.name}
            </Link>
            {org.is_byoc ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                BYOC
              </span>
            ) : null}
          </div>
          <div className="mt-1 font-mono text-xs text-gray-500">{org.id}</div>
          <div className="mt-1 text-xs text-gray-500">
            Due {formatRelativeUtc(new Date(org.purge_at), new Date())} (
            <span className="font-mono">
              {formatUtcTimestamp(org.purge_at)}
            </span>
            ) | {pluralize(org.purge_regions.length, "cluster")}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="destructive"
            size="sm"
            title="Opens a confirmation dialog before creating purge jobs"
            onClick={() => onPurge(org)}
          >
            Purge
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {org.purge_regions.map((region) => {
          const status = getPurgeRegionStatus(region);
          return (
            <div
              key={region.region}
              className="flex flex-wrap items-center justify-between gap-2 rounded bg-white px-3 py-2 text-sm"
            >
              <div>
                <Link
                  to={`/clusters/${region.region}`}
                  className="font-mono font-medium text-gray-900 hover:underline"
                >
                  {region.region}
                </Link>
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}
              >
                {status.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrgsToPurgeCustomerSection({
  group,
  onPurge,
}: {
  group: OrgsToPurgeCustomerGroup;
  onPurge: (org: OrgToPurge) => void;
}) {
  const orgCount = group.orgs.length;
  const clusterCount = new Set(
    group.orgs.flatMap((org) =>
      org.purge_regions.map((region) => region.region),
    ),
  ).size;
  const deactivatedAt = group.customerDeactivatedAt
    ? formatRelativeUtc(new Date(group.customerDeactivatedAt), new Date())
    : "Not deactivated";

  return (
    <div className="space-y-3 rounded-md border border-gray-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 pb-3">
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
          <span>
            Customer:{" "}
            <span className="font-semibold text-gray-900">
              {group.customerName ?? "No customer"}
            </span>
          </span>
          <span>
            ID:{" "}
            <span className="break-all font-mono">
              {group.customerId ?? "None"}
            </span>
          </span>
          <span>Deactivated: {deactivatedAt}</span>
        </div>
        <div className="text-right text-xs text-gray-500">
          {pluralize(orgCount, "org")} | {pluralize(clusterCount, "cluster")}
        </div>
      </div>

      <div className="space-y-3">
        {group.orgs.map((org) => (
          <OrgToPurgeCard key={org.id} org={org} onPurge={onPurge} />
        ))}
      </div>
    </div>
  );
}

export default function OrgsToPurge() {
  const [orgs, setOrgs] = useState<OrgToPurge[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOrg, setSelectedOrg] = useState<OrgToPurge | null>(null);
  const [selectedClusterIds, setSelectedClusterIds] = useState<string[]>([]);
  const [isCreatingJobs, setIsCreatingJobs] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createdOperations, setCreatedOperations] = useState<
    { region: string; operationId: string }[]
  >([]);
  const { status, setIsAuthorized } = useAuth();

  const fetchOrgsToPurge = useCallback(() => {
    setIsLoading(true);
    return api
      .getOrgsToPurge()
      .then((purgeOrgs) => {
        setOrgs(purgeOrgs);
        setError(null);
      })
      .catch((err) => {
        if (err.status === 401) {
          setIsAuthorized(false);
        } else {
          setError(err.message ?? "Failed to fetch orgs to purge");
        }
      })
      .finally(() => setIsLoading(false));
  }, [setIsAuthorized]);

  useEffect(() => {
    fetchOrgsToPurge();
  }, [fetchOrgsToPurge]);

  const selectedPurgeRegions = selectedOrg?.purge_regions ?? [];
  const selectedRegions = selectedPurgeRegions.filter((region) =>
    selectedClusterIds.includes(region.region),
  );
  const customerGroups = useMemo(() => buildCustomerGroups(orgs), [orgs]);

  const openPurgeDialog = (org: OrgToPurge) => {
    setCreateError(null);
    setCreatedOperations([]);
    setSelectedClusterIds(
      org.purge_regions
        .filter(isPurgeRegionActionable)
        .map((region) => region.region),
    );
    setSelectedOrg(org);
  };

  const closePurgeDialog = () => {
    setSelectedOrg(null);
    setSelectedClusterIds([]);
  };

  const toggleSelectedCluster = (clusterId: string, selected: boolean) => {
    setSelectedClusterIds((current) => {
      if (selected) {
        return current.includes(clusterId) ? current : [...current, clusterId];
      }
      return current.filter((id) => id !== clusterId);
    });
  };

  const handleConfirmPurge = async () => {
    if (!selectedOrg) return;

    setIsCreatingJobs(true);
    setCreateError(null);
    setCreatedOperations([]);

    try {
      for (const region of selectedRegions) {
        const operationId = await api.createClusterOperation(region.region, {
          operation: "tidy",
          job: "delete-org",
          args: [selectedOrg.id, "--stage", "--commit"],
        });
        setCreatedOperations((prev) => [
          ...prev,
          { region: region.region, operationId },
        ]);
      }

      await fetchOrgsToPurge();
      closePurgeDialog();
    } catch (err) {
      setCreateError(
        err instanceof Error ? err.message : "Failed to create purge jobs",
      );
    } finally {
      setIsCreatingJobs(false);
    }
  };

  if (status !== "authorized") {
    return <SessionExpired />;
  }

  if (error) {
    return <div className="text-red-500">Error: {error}</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Orgs to Purge</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Orgs whose <code>purge_at</code> timestamp has passed and which
            still have namespace rows in MySQL. Showing up to 1000 orgs at a
            time.
          </p>

          {isLoading ? (
            <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              Loading orgs to purge...
            </div>
          ) : customerGroups.length === 0 ? (
            <div className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
              No orgs currently need purging.
            </div>
          ) : (
            <div className="space-y-4">
              {customerGroups.map((group) => (
                <OrgsToPurgeCustomerSection
                  key={group.customerId ?? NO_CUSTOMER_GROUP}
                  group={group}
                  onPurge={openPurgeDialog}
                />
              ))}
            </div>
          )}
        </div>
      </CardContent>
      <Dialog
        open={selectedOrg !== null}
        onOpenChange={(open) => {
          if (!open && !isCreatingJobs) {
            closePurgeDialog();
          }
        }}
      >
        <DialogContent className="md:max-w-xl">
          <DialogHeader>
            <DialogTitle>Confirm org purge</DialogTitle>
            <DialogDescription>
              This will create a <code>delete-org --stage --commit</code> tidy
              job for the selected org in each checked cluster.
            </DialogDescription>
          </DialogHeader>

          {selectedOrg ? (
            <div className="space-y-3">
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-800">
                Warning: all namespaces will be deleted in object storage.
              </div>

              <div className="rounded bg-gray-50 p-3 text-sm">
                <div className="font-medium">{selectedOrg.name}</div>
                <div className="font-mono text-xs text-gray-500">
                  {selectedOrg.id}
                </div>
              </div>

              <div className="space-y-2">
                {selectedPurgeRegions.map((region) => {
                  const status = getPurgeRegionStatus(region);
                  return (
                    <div
                      key={region.region}
                      className="flex flex-wrap items-center justify-between gap-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm"
                    >
                      <label className="flex min-w-0 items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedClusterIds.includes(region.region)}
                          onChange={(event) =>
                            toggleSelectedCluster(
                              region.region,
                              event.target.checked,
                            )
                          }
                          className="h-4 w-4 cursor-pointer rounded border-gray-300 text-blue-600 focus:ring-black"
                        />
                        <Link
                          to={`/clusters/${region.region}`}
                          className="font-mono text-gray-900 hover:underline"
                        >
                          {region.region}
                        </Link>
                      </label>
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${status.className}`}
                      >
                        {status.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {createdOperations.length > 0 ? (
            <div className="rounded bg-green-50 p-2 text-xs text-green-700">
              Created{" "}
              {createdOperations.map((op) => (
                <span key={op.operationId} className="font-mono">
                  {op.region}: {op.operationId}{" "}
                </span>
              ))}
            </div>
          ) : null}

          {createError ? (
            <div className="rounded bg-red-50 p-2 text-sm text-red-700">
              {createError}
            </div>
          ) : null}

          <DialogFooter className="justify-end">
            <Button
              variant="secondary"
              disabled={isCreatingJobs}
              onClick={closePurgeDialog}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isCreatingJobs || selectedRegions.length === 0}
              onClick={handleConfirmPurge}
            >
              {isCreatingJobs ? (
                <SpinnerIcon className="animate-spin" />
              ) : (
                "Create purge jobs"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
